// game/shared/src/doodad-flatness.ts
//
// Drop decorative doodads whose base would FLOAT over a cliff edge. Pure +
// deterministic, run at every doodad consumer (client render world.ts, server
// collision static-obstacles.ts, server navmesh.ts) right AFTER
// resolveDoodadPlacement, so render == collided == pathed stays true: a doodad
// removed here is removed from the picture, the collision field, and the navmesh
// alike — never a visible-but-uncollidable ghost or an invisible phantom collider.
//
// WHY IT EXISTS. The client exaggerates cliff steps for the KTS wall read
// (terrain.ts exaggerateCliffHeights / cliffVisualOffset). A doodad seated at its
// CENTRE height then has its base hang in the air wherever its footprint spills
// over the raised step. The fix: refuse to place a doodad unless ≥ minFrac of its
// base footprint sits on the SAME plane it's seated on.
//
// "Same plane" = same PLATEAU level (64/128/192). This is deliberately NOT a raw
// height-deviation test: the terrain smoothing (smoothHeightsForVisuals) blurs
// every ramp and ramp↔plateau junction smooth, leaving ONLY hard plateau↔plateau
// lips crisp — so those lips are the only place a base abruptly floats. A doodad
// sitting on a ramp (intermediate cliff levels) or spanning a smoothed junction is
// fine and must be kept; testing crisp plateau-level identity targets exactly the
// abrupt floaters and spares the (far more numerous) ramp/slope doodads.
//
// SCOPE. Only decorative doodads (kind:"doodad"). Harvestable resource NODES
// (kind:"unit") are gameplay-critical and left untouched; they are authored on
// flat ground and dropping one would delete a resource.

import { CLIFF_PLATEAU_LEVELS } from "./cliff-edges";
import { decorFootprint, type PlacementRow } from "./doodad-placement";

export interface FlatnessOptions {
  /** Minimum fraction of footprint samples that must be on the seat's plane to keep the doodad. */
  minFrac?: number;
  /** Footprints with radius ≤ this (m) never float meaningfully — always kept (skips the test). */
  minRadius?: number;
}

const DEFAULTS = { minFrac: 0.85, minRadius: 0.5 } as const;

// A tree's CANOPY may overhang a cliff edge and still look right (branches over a
// drop read fine); only its narrow TRUNK floats. So trees test against a small
// trunk footprint, not their wide canopy radius — which also keeps the float
// filter from thinning the forest the placement resolver works to preserve. Rocks
// and other solid-base doodads use their real footprint (decorFootprint).
const TREE_TRUNK_RADIUS = 0.8; // m, × object scale
const isTreeType = (type: string): boolean => /Tree$/i.test(type);
function baseFootprint(type: string, scale: number): number {
  return isTreeType(type) ? TREE_TRUNK_RADIUS * (scale > 0 ? scale : 1) : decorFootprint(type, scale);
}

// Footprint sample disc: centre + two rings (0.5r, 1.0r) of 8 points each = 17
// samples. Rings (not a square grid) keep the test rotation-fair and cheap.
const RING_RADII = [0.5, 1.0];
const RING_COUNT = 8;

/**
 * Keep only decorative doodads whose base sits on a single plane. `cliffLevels`
 * is the cliffs.bin grid (cellDim×cellDim uint16, 1 cell = 1 world unit), the same
 * buffer the client passes to exaggerateCliffHeights. Non-doodad rows pass through.
 */
export function filterDoodadsOnFlatGround<T extends PlacementRow>(
  rows: T[],
  cliffLevels: Uint16Array,
  opts: FlatnessOptions = {},
): T[] {
  const minFrac = opts.minFrac ?? DEFAULTS.minFrac;
  const minRadius = opts.minRadius ?? DEFAULTS.minRadius;
  const cellDim = Math.round(Math.sqrt(cliffLevels.length));

  const levelAt = (x: number, z: number): number => {
    let c = Math.floor(x);
    let r = Math.floor(z);
    c = c < 0 ? 0 : c >= cellDim ? cellDim - 1 : c;
    r = r < 0 ? 0 : r >= cellDim ? cellDim - 1 : r;
    return cliffLevels[r * cellDim + c]!;
  };

  return rows.filter((o) => {
    if (o.kind !== "doodad") return true;            // harvestables/units: never dropped
    const radius = baseFootprint(o.type, o.scale ?? 1);
    if (radius <= minRadius) return true;            // tiny scatter / thin trunk can't float

    const seatLevel = levelAt(o.x, o.y);             // o.y is the world-Z plane coord
    if (seatLevel === 0) return true;                // void-seated (rare) — leave as authored
    const seatIsPlateau = CLIFF_PLATEAU_LEVELS.has(seatLevel);

    let inPlane = 1;                                  // centre sample (on the seat plane)
    let total = 1;
    for (const rr of RING_RADII) {
      const rad = radius * rr;
      for (let k = 0; k < RING_COUNT; k++) {
        const a = (k / RING_COUNT) * Math.PI * 2;
        const lvl = levelAt(o.x + Math.cos(a) * rad, o.y + Math.sin(a) * rad);
        total++;
        // Off-plane = the base would hang over a crisp, un-smoothed drop:
        //  - OFF-MAP / chasm (level 0): an ~11m pit edge the terrain no longer smooths into; OR
        //  - a DIFFERENT plateau than a plateau-seated doodad (a cliff lip).
        // A ramp sample (intermediate level) is a smoothed walkable slope → on-plane, so a
        // ramp-seated doodad only drops when its footprint spills into a chasm, not where it
        // meets the plateau it ramps onto.
        const offPlane = lvl === 0 || (seatIsPlateau && CLIFF_PLATEAU_LEVELS.has(lvl) && lvl !== seatLevel);
        if (!offPlane) inPlane++;
      }
    }
    return inPlane / total >= minFrac;
  });
}
